import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useLanguage } from './i18n';
import './ServiceManager.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SKM_T = {
  kk: {
    title: '🔑 Қызметтердің қолжетімділік кілттері',
    total: 'Барлық қызметтер:',
    info: '🔒 Кілттер қатаң құпия. Кілтті ауыстырғаннан кейін оны қызмет басшысына хабарлаңыз — ескі кілт бірден жарамсыз болады. Бұрын орындалған кірулер сақталады.',
    loading: 'Жүктелуде...',
    loadError: 'Қызмет кілттерін жүктеу мүмкін болмады',
    retry: 'Қайталау',
    inactive: 'өшірілген',
    placeholder: 'Жаңа кілт (кемінде 8 таңба)',
    save: '💾 Сақтау',
    cancel: 'Болдырмау',
    hide: '🙈 Жасыру',
    show: '👁 Көрсету',
    hideTitle: 'Жасыру',
    showTitle: 'Көрсету',
    edit: '✏️ Өзгерту',
    done: 'Дайын',
    close: 'Жабу',
    minKeyError: 'Кілт кемінде 8 таңбадан тұруы керек',
    saveError: 'Кілтті сақтау қатесі'
  },
  ru: {
    title: '🔑 Ключи доступа служб',
    total: 'Всего служб:',
    info: '🔒 Ключи строго конфиденциальны. После смены ключа сообщите его руководителю службы — старый ключ перестанет действовать сразу. Уже выполненные входы сохраняются.',
    loading: 'Загрузка...',
    loadError: 'Не удалось загрузить ключи служб',
    retry: 'Повторить',
    inactive: 'отключена',
    placeholder: 'Новый ключ (мин. 8 символов)',
    save: '💾 Сохранить',
    cancel: 'Отмена',
    hide: '🙈 Скрыть',
    show: '👁 Показать',
    hideTitle: 'Скрыть',
    showTitle: 'Показать',
    edit: '✏️ Изменить',
    done: 'Готово',
    close: 'Закрыть',
    minKeyError: 'Ключ должен содержать минимум 8 символов',
    saveError: 'Ошибка сохранения ключа'
  }
};

const maskKey = (key) => {
  if (!key) return '';
  if (key.length <= 4) return '••••';
  return key.slice(0, 2) + '•'.repeat(Math.max(key.length - 4, 4)) + key.slice(-2);
};

const ServiceKeyManager = ({ onClose }) => {
  const { language } = useLanguage();
  const t = SKM_T[language];
  const [keys, setKeys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [visibleKey, setVisibleKey] = useState(null); // service_name с открытым ключом
  const [editingKey, setEditingKey] = useState(null); // service_name в режиме редактирования
  const [newKeyValue, setNewKeyValue] = useState('');
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);

  const loadKeys = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await axios.get(`${API}/admin/service-keys`);
      setKeys(response.data.keys || []);
    } catch (err) {
      console.error('Load service keys error:', err);
      setError(t.loadError);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadKeys();
  }, [loadKeys]);

  const startEdit = (key) => {
    setEditingKey(key.service_name);
    setNewKeyValue(key.access_key);
    setSaveError(null);
  };

  const cancelEdit = () => {
    setEditingKey(null);
    setNewKeyValue('');
    setSaveError(null);
  };

  const saveKey = async (serviceName) => {
    const trimmed = newKeyValue.trim();
    if (trimmed.length < 8) {
      setSaveError(t.minKeyError);
      return;
    }
    setSaving(true);
    setSaveError(null);
    try {
      await axios.patch(`${API}/admin/service-keys/${serviceName}`, {
        access_key: trimmed
      });
      setKeys(prev => prev.map(k =>
        k.service_name === serviceName ? { ...k, access_key: trimmed } : k
      ));
      setEditingKey(null);
      setNewKeyValue('');
    } catch (err) {
      console.error('Save key error:', err);
      setSaveError(err.response?.data?.detail || t.saveError);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="sm-overlay" data-testid="service-key-manager-modal" onClick={onClose}>
      <div className="sm-panel" onClick={(e) => e.stopPropagation()}>
        <div className="sm-header">
          <div>
            <h2 className="sm-title">{t.title}</h2>
            <p className="sm-subtitle">{t.total} <strong>{keys.length}</strong></p>
          </div>
          <button
            className="sm-close-btn"
            onClick={onClose}
            data-testid="service-key-manager-close"
            aria-label={t.close}
          >
            ✕
          </button>
        </div>

        <div className="sm-info">
          {t.info}
        </div>

        {loading && (
          <div className="sm-loading" data-testid="skm-loading">
            <div className="sm-spinner"></div>
            <p>{t.loading}</p>
          </div>
        )}

        {error && (
          <div className="sm-error" data-testid="skm-error">
            {error}
            <button onClick={loadKeys} className="sm-retry-btn">{t.retry}</button>
          </div>
        )}

        {!loading && !error && (
          <div className="sm-list" data-testid="skm-keys-list">
            {keys.map(key => (
              <div
                key={key.service_name}
                className={`sm-item skm-item ${key.is_active ? 'enabled' : 'disabled'}`}
                data-testid={`skm-key-${key.service_name}`}
              >
                <div className="sm-item-info">
                  <div className="sm-item-text">
                    <div className="sm-item-name">
                      {key.description || key.service_name}
                      {!key.is_active && <span className="sm-badge skm-badge-inactive">{t.inactive}</span>}
                    </div>
                    <div className="sm-item-meta">
                      <span className="sm-item-kk">{key.service_name}</span>
                    </div>

                    {editingKey === key.service_name ? (
                      <div className="skm-edit-row">
                        <input
                          type="text"
                          className="skm-input"
                          value={newKeyValue}
                          onChange={(e) => setNewKeyValue(e.target.value)}
                          placeholder={t.placeholder}
                          disabled={saving}
                          data-testid={`skm-input-${key.service_name}`}
                        />
                        <button
                          className="skm-btn skm-btn-save"
                          onClick={() => saveKey(key.service_name)}
                          disabled={saving}
                          data-testid={`skm-save-${key.service_name}`}
                        >
                          {saving ? '...' : t.save}
                        </button>
                        <button
                          className="skm-btn skm-btn-cancel"
                          onClick={cancelEdit}
                          disabled={saving}
                          data-testid={`skm-cancel-${key.service_name}`}
                        >
                          {t.cancel}
                        </button>
                        {saveError && editingKey === key.service_name && (
                          <div className="skm-save-error" data-testid="skm-save-error">{saveError}</div>
                        )}
                      </div>
                    ) : (
                      <div className="skm-key-row">
                        <code className="skm-key-value" data-testid={`skm-value-${key.service_name}`}>
                          {visibleKey === key.service_name ? key.access_key : maskKey(key.access_key)}
                        </code>
                        <button
                          className="skm-btn"
                          onClick={() => setVisibleKey(visibleKey === key.service_name ? null : key.service_name)}
                          title={visibleKey === key.service_name ? t.hideTitle : t.showTitle}
                          data-testid={`skm-show-${key.service_name}`}
                        >
                          {visibleKey === key.service_name ? t.hide : t.show}
                        </button>
                        <button
                          className="skm-btn"
                          onClick={() => startEdit(key)}
                          data-testid={`skm-edit-${key.service_name}`}
                        >
                          {t.edit}
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        <div className="sm-footer">
          <button className="sm-action-btn" onClick={onClose} data-testid="skm-done-btn">
            {t.done}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ServiceKeyManager;
